import React, { useState } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Button from './ui/Button';
import Alert from './ui/Alert';
import AutoRoleCreator from '../utils/autoCreateAdvancedRoles';

/**
 * Composant pour initialiser automatiquement les rôles avancés
 */
const AutoRoleSetup = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleCreateRoles = async () => {
    if (!user) {
      setError('Vous devez être connecté pour effectuer cette opération');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const results = await AutoRoleCreator.createAllDefaultRoles(user.uid);
      setResult(results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const status = await AutoRoleCreator.checkAdvancedRolesStatus();
      setResult(status);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateUser = async () => {
    if (!user) {
      setError('Vous devez être connecté pour effectuer cette opération');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const results = await AutoRoleCreator.migrateUserToAdvancedRole(user.uid);
      setResult(results);
      
      if (results.success) {
        // Recharger la page pour que les nouvelles permissions prennent effet
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="text-center py-4">Chargement...</div>;
  }

  return (
    <div className="max-w-md mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        Initialisation Automatique des Rôles
      </h2>
      
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        Utilisez cet outil pour créer automatiquement tous les rôles du nouveau système avancé.
      </p>

      {error && (
        <Alert type="error" className="mb-4">
          {error}
        </Alert>
      )}

      {result && (
        <Alert 
          type={result.errors && result.errors.length > 0 ? 'warning' : 'success'} 
          className="mb-4"
        >
          <pre className="text-sm whitespace-pre-wrap">
            {JSON.stringify(result, null, 2)}
          </pre>
        </Alert>
      )}

      <div className="space-y-3">
        <Button
          onClick={handleCreateRoles}
          isLoading={loading}
          className="w-full"
          variant="primary"
        >
          Créer Tous les Rôles
        </Button>

        <Button
          onClick={handleCheckStatus}
          isLoading={loading}
          className="w-full"
          variant="outline"
        >
          Vérifier l'État
        </Button>

        <Button
          onClick={handleMigrateUser}
          isLoading={loading}
          className="w-full"
          variant="secondary"
        >
          Me Migrer vers Nouveau Système
        </Button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
          ℹ️ Instructions
        </h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700 dark:text-blue-300">
          <li>Cliquez sur "Créer Tous les Rôles" pour initialiser le système</li>
          <li>Vérifiez l'état avec "Vérifier l'État"</li>
          <li>Migrez votre utilisateur vers le nouveau système</li>
          <li>La page se rechargera automatiquement après migration</li>
        </ol>
      </div>

      {user && (
        <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Utilisateur: {user.email}
            <br />
            ID: {user.uid}
          </p>
        </div>
      )}
    </div>
  );
};

export default AutoRoleSetup;
